import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Баннер приглашения в школу.
 * Показывается, когда пользователь открыл приложение по ссылке
 * t.me/<bot>?startapp=inv_<token> и ещё не состоит в школе.
 */
export default function JoinSchoolBanner() {
  const { inviteToken, user, joinSchool, dismissInvite } = useAuth();
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Уже в школе (или владелец) — баннер не нужен.
  // После успешного join показываем подтверждение даже если school_id уже задан.
  if (!done && (!inviteToken || user?.school_id)) return null;

  const handleClose = () => {
    setDone(false);
    dismissInvite();
  };

  const handleJoin = async () => {
    if (!inviteToken) return;
    setJoining(true);
    setError(null);
    try {
      await joinSchool(inviteToken);
      setDone(true);
    } catch (e) {
      const raw = e instanceof Error ? e.message : '';
      if (raw.includes('409')) setError('Вы уже принадлежите другой школе');
      else if (raw.includes('404')) setError('Ссылка-приглашение недействительна');
      else setError('Не удалось присоединиться. Попробуйте ещё раз.');
      setJoining(false);
    }
  };

  return (
    <div className="sticky top-0 z-40 px-4 pt-3 pb-2">
      <div
        className={`relative overflow-hidden rounded-2xl p-4 shadow-lg animate-fade-in ${
          done
            ? 'bg-gradient-to-r from-emerald-500/15 to-teal-500/15 border border-emerald-500/20'
            : 'bg-gradient-to-r from-[var(--tg-theme-button-color)]/15 to-purple-600/15 border border-[var(--tg-theme-button-color)]/25'
        }`}
      >
        <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full bg-gradient-to-br from-purple-500/20 to-[var(--tg-theme-button-color)]/10 blur-2xl" />
        <div className="relative z-10">
          {done ? (
            <div className="flex items-center gap-3">
              <span className="text-2xl">🎉</span>
              <div className="flex-1">
                <p className="text-sm font-semibold text-[var(--tg-theme-text-color)]">
                  Вы присоединились к школе!
                </p>
                <p className="text-xs text-[var(--tg-theme-hint-color)] mt-0.5">
                  Теперь вы часть команды. Раздел «Команда» виден владельцу школы.
                </p>
              </div>
              <button
                onClick={handleClose}
                className="text-xs font-medium text-[var(--tg-theme-button-color)] shrink-0"
              >
                Ок
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <span className="text-2xl">🏫</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[var(--tg-theme-text-color)]">
                  Вас пригласили в школу
                </p>
                <p className="text-xs text-[var(--tg-theme-hint-color)] mt-0.5">
                  {error || 'Присоединиться к команде?'}
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={dismissInvite}
                  disabled={joining}
                  className="px-3 py-1.5 rounded-xl text-xs font-medium text-[var(--tg-theme-hint-color)] transition-all active:scale-95 disabled:opacity-50"
                >
                  Позже
                </button>
                <button
                  onClick={handleJoin}
                  disabled={joining}
                  className="px-3 py-1.5 rounded-xl bg-[var(--tg-theme-button-color)] text-[var(--tg-theme-button-text-color)] text-xs font-semibold transition-all active:scale-95 disabled:opacity-60 flex items-center gap-1.5"
                >
                  {joining && <span className="w-3 h-3 rounded-full border-2 border-current border-t-transparent animate-spin" />}
                  Присоединиться
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
